import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import moment from "moment";
import { Icon, Message } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { LabelStackView } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";

import util from "utils/utils";

import { StatusLabel } from "../../Cards/ChallengeOverview/styles";
import { IdeaCoverImage } from "../../Cards/IdeaCard/ListView/styles";
import { UserChip } from "../../Chips";
import ConfigurableTable from "../../ConfigurableTable";
import Tag, { TagContainer } from "../../Tag";

type IdeasProps = {
  businessProfile: OpenAPI.GET<"/users/profiles/{business_profile_id}">["response"]["ideaBusinessProfile"];
  closeModal: () => void;
};

export const Ideas = ({ businessProfile }: IdeasProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!businessProfile.ownerIdeas) {
    return (
      <Message info>
        <Message.Header>No ideas found</Message.Header>
        <p>This organisation has not been attached to any ideas yet.</p>
      </Message>
    );
  }

  return (
    <ConfigurableTable
      tableKey="businessProfileIdeas"
      data={businessProfile.ownerIdeas ?? []}
      keyExtractor={(idea) => idea._id}
      onRowClick={(idea) => {
        util
          .confirm(
            "Do you want to view this idea?",
            "You will leave the current page and will navigate to the idea. Proceed?",
          )
          .then(() => navigate(`/ideas/${idea._id}`))
          .catch(() => {});
      }}
      columns={[
        {
          key: "image",
          name: "",
          settingName: "Image",
          width: 40,
          // @ts-ignore
          render: ({ item }) => (
            <IdeaCoverImage
              $coverImage={util.ideaCoverImage(item, "200x200")}
            />
          ),
        },
        {
          key: "name",
          name: "Name",
          collapsing: true,
          render: ({ cell }) => <b>{cell}</b>,
        },
        {
          key: "owner",
          name: "User",
          render: ({ cell }) => <UserChip user={cell} compact />,
        },
        {
          key: "collaborators",
          name: "Collaborators",
          render: ({ item }) => (
            <div style={{ display: "flex", flexDirection: "row" }}>
              {item?.authors
                ?.filter((a) => a._id !== item.user)
                .map((a) => (
                  <UserChip key={a._id} user={a} compact />
                ))}
            </div>
          ),
        },
        {
          key: "challenge",
          name: t("common:capitalise", { key: "generic.challenge" }),
          collapsing: true,
          render: ({ item }) => (
            <>
              <Icon name="target" />
              {item?.ownerChallenge?.name ?
                <Link to={`/challenges/${item.challenge}`}>
                  {item.ownerChallenge.name}
                </Link>
              : <span>
                  {t("common:capitalise", { key: "generic.challenge" })} deleted
                </span>
              }
            </>
          ),
        },
        {
          key: "status",
          name: "Status",
          render: ({ item }) => (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {/* @ts-ignore */}
              <StatusLabel color={item.isSubmitted ? "green" : "yellow"} />
              {item.isSubmitted ? "Submitted" : "Draft"}
            </div>
          ),
        },
        {
          key: "updatedAt",
          name: "Last update",
          collapsing: true,
          render: ({ cell }) => moment(cell).format("DD/MM/YY"),
        },
        {
          key: "submittedAt",
          name: "Submitted on",
          collapsing: true,
          render: ({ cell }) => moment(cell).format("DD/MM/YY"),
        },
        {
          key: "labels",
          name: "Labels",
          render: ({ cell }) => <LabelStackView labels={cell} horizontal />,
        },
        {
          key: "tags",
          name: "Tags",
          render: ({ item }) => (
            <TagContainer>
              {/* @ts-ignore */}
              {item.ownerTags?.map((tag) => (
                <Tag tag={tag} key={tag.value} />
              ))}
            </TagContainer>
          ),
        },
        {
          key: "comments",
          name: "Comments",
          render: ({ item }) =>
            item.ownerChallenge?.preventIdeaComments ?
              <span>Disabled</span>
            : <span>{item.commentCount || 0}</span>,
        },
        {
          key: "likes",
          name: "Likes",
          render: ({ item }) => <span>{item.voteCount || 0}</span>,
        },
      ]}
    />
  );
};
